import { useMemo } from "react";
import { useSession } from "next-auth/react";
import type { Session } from "next-auth";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface UseAuthReturn {
  /** The raw session object. null if unauthenticated, undefined while loading. */
  session: Session | null | undefined;
  /** The session user object. Shorthand for session?.user. */
  user: Session["user"] | undefined;
  /** The JWT access token. Shorthand for session?.accessToken. */
  accessToken: string | undefined;
  /** The raw status string from useSession: "loading" | "authenticated" | "unauthenticated". */
  status: AuthStatus;
  /** True while the session is being fetched. */
  isLoading: boolean;
  /** True when status === "authenticated" (session exists). */
  isAuthenticated: boolean;
  /**
   * True when the user is fully logged in (not a guest, not a soft-login).
   * Mirrors the logic from useIsLoggedIn.
   */
  isLoggedIn: boolean;
  /** True when the session belongs to a guest user. */
  isGuest: boolean;
  /** Update the session. Delegates to useSession().update(). */
  update: ReturnType<typeof useSession>["update"];
}

/**
 * Centralized auth hook that wraps next-auth's useSession.
 *
 * Provides the raw session plus commonly-derived values (accessToken, user,
 * isLoggedIn, isGuest, isLoading) so consumers don't need to repeat the
 * same destructuring and boolean derivations.
 *
 * @example
 * // Instead of:
 * const { data: session } = useSession();
 * const token = session?.accessToken;
 * const email = session?.user?.email;
 *
 * // Use:
 * const { accessToken, user } = useAuth();
 * const email = user?.email;
 */
export function useAuth(): UseAuthReturn {
  const { data: session, status, update } = useSession();

  const derived = useMemo(() => {
    const user = session?.user;
    const accessToken = session?.accessToken;
    const signInPlatform = user?.signInPlatform;

    return {
      user,
      accessToken,
      isLoading: status === "loading",
      isAuthenticated: status === "authenticated",
      isLoggedIn:
        signInPlatform !== AuthProvider.CREDENTIALS_SOFT_LOGIN &&
        signInPlatform !== AuthProvider.GUEST_LOGIN &&
        !!accessToken,
      isGuest: signInPlatform === AuthProvider.GUEST_LOGIN,
    };
  }, [session, status]);

  return {
    session: session ?? null,
    status,
    update,
    ...derived,
  };
}
